<template>
  <div class="dice-labeler">
    <!-- Access denied overlay -->
    <div v-if="accessDenied" class="access-denied">
      <div class="access-icon">🔒</div>
      <h2>Training Access Required</h2>
      <p>You need moderator access to use the training tools.</p>
      <p class="hint">Contact an administrator to request access.</p>
      <router-link to="/" class="btn blue" style="display: inline-block; margin-top: 16px; text-decoration: none;">
        Back to Home
      </router-link>
    </div>

    <!-- No frames available -->
    <div v-if="!accessDenied && !loading && frames.length === 0" class="access-denied">
      <div class="access-icon">📭</div>
      <h2>No Frames Available</h2>
      <p>There are no frames ready for labeling yet.</p>
      <p class="hint">An admin must process an uploaded video first.</p>
      <router-link to="/training/upload" class="btn blue" style="display: inline-block; margin-top: 16px; text-decoration: none;">
        Upload a Video
      </router-link>
    </div>

    <!-- Header bar -->
    <div v-if="!accessDenied && frames.length > 0" class="header-bar">
      <div class="header-left">
        <span class="logo">⚄</span>
        <span class="title">Dice Labeler</span>
        <span class="counter">{{ currentIdx + 1 }} / {{ frames.length }}</span>
        <span class="stat green">{{ stats?.labeledFrames || 0 }} labeled</span>
        <span class="stat">{{ stats?.totalBoxes || 0 }} boxes</span>
        <span v-if="currentFrame?.videoName" class="stat">| {{ currentFrame.videoName }}</span>
      </div>
      <div class="header-right">
        <router-link to="/training/upload" class="btn-nav">
          Upload Video
        </router-link>
      </div>
    </div>

    <!-- Main content -->
    <div v-if="!accessDenied && frames.length > 0" class="main-content">
      <!-- Canvas area -->
      <div class="canvas-area" ref="canvasContainer">
        <canvas
          ref="canvas"
          @mousedown="onMouseDown"
          @mousemove="onMouseMove"
          @mouseup="onMouseUp"
        />

        <!-- Error overlay -->
        <div v-if="imageError" class="error-overlay">
          <div class="error-title">Image failed to load</div>
          <div class="error-url">{{ currentFrameSrc }}</div>
          <div class="error-hint">Check that the API route is deployed</div>
        </div>

        <!-- Box count overlay -->
        <div v-if="currentBoxes.length > 0" class="box-overlay">
          <span v-for="(box, i) in currentBoxes" :key="i" :style="{ color: diceColors[box.cls] }">
            d{{ box.cls + 1 }}
          </span>
        </div>

        <!-- Save status -->
        <div v-if="currentSaveStatus" class="save-badge" :class="currentSaveStatus">
          {{ currentSaveStatus }}
        </div>

        <!-- Loading indicators -->
        <div v-if="loadingLabel" class="loading-label">Loading labels...</div>
        <div v-if="loadingImage" class="loading-label">Loading image...</div>
      </div>

      <!-- Right panel -->
      <div class="right-panel">
        <!-- Die value selector -->
        <div class="section-label">Die Value</div>
        <div class="dice-grid">
          <button
            v-for="dv in diceValues"
            :key="dv.id"
            class="dice-btn"
            :class="{ active: selectedValue === dv.id }"
            :style="selectedValue === dv.id ? { background: diceColors[dv.id], borderColor: diceColors[dv.id] } : {}"
            @click="selectedValue = dv.id"
          >
            <span class="pip">{{ dv.pips }}</span>
            <span class="key">{{ dv.key }}</span>
          </button>
        </div>

        <!-- Distribution -->
        <div class="section-label">Distribution</div>
        <div v-for="(count, i) in displayClassCounts" :key="i" class="dist-row">
          <span class="dist-label" :style="{ color: diceColors[i] }">d{{ i + 1 }}</span>
          <div class="dist-bar-bg">
            <div class="dist-bar" :style="{ width: barWidth(count), background: diceColors[i] }" />
          </div>
          <span class="dist-count">{{ count }}</span>
        </div>

        <!-- Actions -->
        <div class="actions">
          <button class="btn blue" @click="saveCurrentFrame">Save Frame (⌘S)</button>
          <button class="btn grey" @click="skipFrame">Skip — No Dice (N)</button>
          <button class="btn dark" @click="undoLastBox">Undo Last Box (⌘Z)</button>
        </div>

        <!-- Spacer -->
        <div style="flex: 1" />

        <!-- Navigation -->
        <div class="section-label">Navigation</div>
        <div class="nav-row">
          <button class="btn dark" @click="prev" :disabled="currentIdx === 0">← A</button>
          <button class="btn dark" @click="next" :disabled="currentIdx >= frames.length - 1">D →</button>
        </div>
        <input
          type="range"
          class="scrubber"
          :min="0"
          :max="frames.length - 1"
          v-model.number="currentIdx"
        />

        <!-- Shortcuts -->
        <div class="shortcuts">
          <div><kbd>1-6</kbd> select die value</div>
          <div><kbd>←/→</kbd> navigate</div>
          <div><kbd>⌘S</kbd> save frame</div>
          <div><kbd>⌘Z</kbd> undo last box</div>
          <div><kbd>N</kbd> skip (no dice)</div>
          <div><kbd>drag</kbd> draw bounding box</div>
        </div>
      </div>
    </div>

    <!-- Snackbar -->
    <div v-if="snackbar" class="snackbar" :class="snackbarColor">
      {{ snackbarText }}
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'

const API_BASE = process.env.VUE_APP_API_URL || 'https://api.backgammontracker.com'

const diceValues = [
  { id: 0, label: '1', pips: '⚀', key: '1' },
  { id: 1, label: '2', pips: '⚁', key: '2' },
  { id: 2, label: '3', pips: '⚂', key: '3' },
  { id: 3, label: '4', pips: '⚃', key: '4' },
  { id: 4, label: '5', pips: '⚄', key: '5' },
  { id: 5, label: '6', pips: '⚅', key: '6' },
]

const diceColors = ['#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899']

// ─── Auth ───────────────────────────────────────────────────

const getAuthHeaders = (includeContentType = false) => {
  const token = localStorage.getItem('accessToken')
  const headers = {}
  if (token) headers['Authorization'] = `Bearer ${token}`
  if (includeContentType) headers['Content-Type'] = 'application/json'
  return headers
}

const accessDenied = ref(false)

// ─── State ──────────────────────────────────────────────────

const frames = ref([])
const currentIdx = ref(0)
const boxes = ref({})          // keyed by frameId (integer)
const selectedValue = ref(3)
const loading = ref(true)
const loadingLabel = ref(false)
const loadingImage = ref(false)
const saved = ref({})          // keyed by frameId
const drawing = ref(null)
const imageError = ref(false)
const snackbar = ref(false)
const snackbarText = ref('')
const snackbarColor = ref('green')
const stats = ref(null)
const fetchedLabels = ref(new Set())

// Current image as blob URL
const currentImageUrl = ref(null)

// ─── Refs ───────────────────────────────────────────────────

const canvas = ref(null)
const canvasContainer = ref(null)

// ─── Computed ───────────────────────────────────────────────

const currentFrame = computed(() => frames.value[currentIdx.value])
const currentBoxes = computed(() => currentFrame.value ? (boxes.value[currentFrame.value.id] || []) : [])
const currentSaveStatus = computed(() => currentFrame.value ? saved.value[currentFrame.value.id] : null)
const currentFrameSrc = computed(() =>
  currentFrame.value ? `${API_BASE}/api/training/image/${currentFrame.value.filename}` : ''
)

// Use server stats for distribution (more accurate than local counting)
const displayClassCounts = computed(() => {
  if (stats.value?.classCounts) return stats.value.classCounts
  return [0, 0, 0, 0, 0, 0]
})

const maxClassCount = computed(() => Math.max(...displayClassCounts.value, 1))
const barWidth = (count) => maxClassCount.value > 0 ? (count / maxClassCount.value * 100) + '%' : '0%'

// ─── Load frames ────────────────────────────────────────────

const loadFrames = async () => {
  loading.value = true
  try {
    const res = await fetch(`${API_BASE}/api/training/frames?unlabeled=true`, {
      headers: getAuthHeaders()
    })

    if (res.status === 401 || res.status === 403) {
      accessDenied.value = true
      loading.value = false
      return
    }

    const data = await res.json()
    frames.value = data.frames || []
    stats.value = data.stats || null
  } catch (e) {
    console.error('Failed to load frames:', e)
    frames.value = []
  }
  loading.value = false

  if (frames.value.length > 0) {
    await maybeLoadLabels(frames.value[currentIdx.value])
    await loadImageWithAuth()
  }
}

// ─── Load image with auth ───────────────────────────────────

const loadImageWithAuth = async () => {
  if (!currentFrame.value) return

  loadingImage.value = true
  imageError.value = false

  // Revoke previous blob URL
  if (currentImageUrl.value) {
    URL.revokeObjectURL(currentImageUrl.value)
    currentImageUrl.value = null
  }

  try {
    const res = await fetch(
      `${API_BASE}/api/training/image/${currentFrame.value.filename}`,
      { headers: getAuthHeaders() }
    )

    if (!res.ok) {
      imageError.value = true
      loadingImage.value = false
      return
    }

    const blob = await res.blob()
    currentImageUrl.value = URL.createObjectURL(blob)

    const img = new Image()
    img.onload = () => {
      loadingImage.value = false
      canvas.value._loadedImg = img
      nextTick(draw)
    }
    img.onerror = () => {
      imageError.value = true
      loadingImage.value = false
    }
    img.src = currentImageUrl.value
  } catch (e) {
    imageError.value = true
    loadingImage.value = false
  }
}

// ─── Load existing labels for a frame ───────────────────────

const maybeLoadLabels = async (frame) => {
  if (!frame) return
  if (!frame.labeled) return
  if (boxes.value[frame.id] !== undefined) return
  if (fetchedLabels.value.has(frame.id)) return

  loadingLabel.value = true
  try {
    const res = await fetch(`${API_BASE}/api/training/labels/${frame.id}`, {
      headers: getAuthHeaders()
    })
    const data = await res.json()
    fetchedLabels.value.add(frame.id)
    if (data.boxes && data.boxes.length > 0) {
      // API returns pixel coordinates { x, y, w, h, cls }
      boxes.value = { ...boxes.value, [frame.id]: data.boxes }
      saved.value = { ...saved.value, [frame.id]: 'saved' }
    }
  } catch (e) {
    console.error('Failed to load labels for frame', frame.id, e)
  }
  loadingLabel.value = false
  nextTick(draw)
}

// ─── Canvas drawing ─────────────────────────────────────────

const draw = () => {
  const cvs = canvas.value
  if (!cvs) return
  const img = cvs._loadedImg
  if (!img || !img.complete || !img.naturalWidth) return

  const container = canvasContainer.value
  if (!container) return

  cvs.width = container.clientWidth
  cvs.height = container.clientHeight
  const ctx = cvs.getContext('2d')

  const scale = Math.min(cvs.width / img.naturalWidth, cvs.height / img.naturalHeight)
  const iw = img.naturalWidth * scale
  const ih = img.naturalHeight * scale
  const ox = (cvs.width - iw) / 2
  const oy = (cvs.height - ih) / 2

  ctx.fillStyle = '#080808'
  ctx.fillRect(0, 0, cvs.width, cvs.height)
  ctx.drawImage(img, ox, oy, iw, ih)

  cvs._imgTransform = { ox, oy, scale }

  currentBoxes.value.forEach((box) => {
    const bx = box.x * scale + ox
    const by = box.y * scale + oy
    const bw = box.w * scale
    const bh = box.h * scale
    const color = diceColors[box.cls]

    ctx.strokeStyle = color
    ctx.lineWidth = 2
    ctx.strokeRect(bx, by, bw, bh)

    ctx.fillStyle = color
    ctx.font = 'bold 13px monospace'
    const label = `d${box.cls + 1}`
    const tm = ctx.measureText(label)
    ctx.fillRect(bx, by - 18, tm.width + 8, 18)
    ctx.fillStyle = '#000'
    ctx.fillText(label, bx + 4, by - 4)

    // Delete button
    ctx.fillStyle = 'rgba(255,0,0,0.8)'
    ctx.fillRect(bx + bw - 14, by, 14, 14)
    ctx.fillStyle = '#fff'
    ctx.font = '11px monospace'
    ctx.fillText('×', bx + bw - 10, by + 11)
  })

  // Drawing preview
  if (drawing.value && drawing.value.curX !== undefined) {
    ctx.strokeStyle = diceColors[selectedValue.value]
    ctx.lineWidth = 2
    ctx.setLineDash([5, 5])
    const x = Math.min(drawing.value.startX, drawing.value.curX)
    const y = Math.min(drawing.value.startY, drawing.value.curY)
    const w = Math.abs(drawing.value.curX - drawing.value.startX)
    const h = Math.abs(drawing.value.curY - drawing.value.startY)
    ctx.strokeRect(x, y, w, h)
    ctx.setLineDash([])
  }
}

// ─── Coordinate conversion ──────────────────────────────────

const canvasToImage = (cx, cy) => {
  const cvs = canvas.value
  if (!cvs || !cvs._imgTransform) return { x: cx, y: cy }
  const { ox, oy, scale } = cvs._imgTransform
  return { x: (cx - ox) / scale, y: (cy - oy) / scale }
}

// ─── Mouse handlers ─────────────────────────────────────────

const getCanvasPos = (e) => {
  const rect = canvas.value.getBoundingClientRect()
  return { x: e.clientX - rect.left, y: e.clientY - rect.top }
}

const onMouseDown = (e) => {
  const pos = getCanvasPos(e)
  const cvs = canvas.value
  if (!cvs._imgTransform) return

  const { scale, ox, oy } = cvs._imgTransform

  // Check if clicking delete button on a box
  for (let i = currentBoxes.value.length - 1; i >= 0; i--) {
    const box = currentBoxes.value[i]
    const bx = box.x * scale + ox + box.w * scale - 14
    const by = box.y * scale + oy
    if (pos.x >= bx && pos.x <= bx + 14 && pos.y >= by && pos.y <= by + 14) {
      const newBoxes = currentBoxes.value.filter((_, idx) => idx !== i)
      boxes.value = { ...boxes.value, [currentFrame.value.id]: newBoxes }
      nextTick(draw)
      return
    }
  }

  drawing.value = { startX: pos.x, startY: pos.y }
}

const onMouseMove = (e) => {
  if (!drawing.value) return
  const pos = getCanvasPos(e)
  drawing.value = { ...drawing.value, curX: pos.x, curY: pos.y }
  draw()
}

const onMouseUp = (e) => {
  if (!drawing.value) return
  const pos = getCanvasPos(e)

  const start = canvasToImage(drawing.value.startX, drawing.value.startY)
  const end = canvasToImage(pos.x, pos.y)

  const x = Math.min(start.x, end.x)
  const y = Math.min(start.y, end.y)
  const w = Math.abs(end.x - start.x)
  const h = Math.abs(end.y - start.y)

  drawing.value = null
  if (w < 3 || h < 3) return

  // Store as pixel coordinates (converted to normalized on save)
  const newBox = { x, y, w, h, cls: selectedValue.value }
  const frameId = currentFrame.value.id
  const updated = [...(boxes.value[frameId] || []), newBox]
  boxes.value = { ...boxes.value, [frameId]: updated }
  nextTick(draw)
}

// ─── Navigation ─────────────────────────────────────────────

const next = () => { if (currentIdx.value < frames.value.length - 1) currentIdx.value++ }
const prev = () => { if (currentIdx.value > 0) currentIdx.value-- }

// ─── Save / Skip ────────────────────────────────────────────

const showSnack = (text, color = 'green') => {
  snackbarText.value = text
  snackbarColor.value = color
  snackbar.value = true
  setTimeout(() => { snackbar.value = false }, 2000)
}

const saveCurrentFrame = async () => {
  if (!currentFrame.value) return

  const frame = currentFrame.value
  const frameBoxes = currentBoxes.value
  const imgW = frame.width || 1280
  const imgH = frame.height || 720

  // Convert pixel coords to normalized YOLO format for the DB
  const normalizedBoxes = frameBoxes.map(b => ({
    cls: b.cls,
    cx: (b.x + b.w / 2) / imgW,
    cy: (b.y + b.h / 2) / imgH,
    w: b.w / imgW,
    h: b.h / imgH,
  }))

  try {
    await fetch(`${API_BASE}/api/training/labels`, {
      method: 'POST',
      headers: getAuthHeaders(true),
      body: JSON.stringify({
        frameId: frame.id,
        boxes: normalizedBoxes,
      }),
    })
    saved.value = { ...saved.value, [frame.id]: 'saved' }
    fetchedLabels.value.add(frame.id)

    // Update local frame labeled state
    const idx = frames.value.findIndex(f => f.id === frame.id)
    if (idx >= 0) frames.value[idx].labeled = true

    showSnack(`Saved frame #${frame.id} (${normalizedBoxes.length} boxes)`)

    // Refresh stats
    refreshStats()
  } catch (e) {
    saved.value = { ...saved.value, [frame.id]: 'local' }
    showSnack('Save failed — stored locally', 'orange')
  }
}

const skipFrame = async () => {
  if (!currentFrame.value) return

  const frame = currentFrame.value

  try {
    await fetch(`${API_BASE}/api/training/labels`, {
      method: 'POST',
      headers: getAuthHeaders(true),
      body: JSON.stringify({
        frameId: frame.id,
        skip: true,
      }),
    })
    boxes.value = { ...boxes.value, [frame.id]: [] }
    saved.value = { ...saved.value, [frame.id]: 'skipped' }

    // Update local frame state
    const idx = frames.value.findIndex(f => f.id === frame.id)
    if (idx >= 0) {
      frames.value[idx].labeled = false
      frames.value[idx].skipped = true
    }

    showSnack(`Skipped frame #${frame.id}`)
    next()
  } catch (e) {
    boxes.value = { ...boxes.value, [frame.id]: [] }
    saved.value = { ...saved.value, [frame.id]: 'skipped' }
    next()
  }
}

const undoLastBox = () => {
  if (!currentFrame.value || currentBoxes.value.length === 0) return
  boxes.value = { ...boxes.value, [currentFrame.value.id]: currentBoxes.value.slice(0, -1) }
  nextTick(draw)
}

// Refresh stats from server (non-blocking)
const refreshStats = async () => {
  try {
    const res = await fetch(`${API_BASE}/api/training/frames?statsOnly=1`, {
      headers: getAuthHeaders()
    })
    const data = await res.json()
    stats.value = data.stats || stats.value
  } catch {}
}

// ─── Keyboard ───────────────────────────────────────────────

const handleKeydown = (e) => {
  if (accessDenied.value) return
  if (e.key >= '1' && e.key <= '6') selectedValue.value = parseInt(e.key) - 1
  else if (e.key === 'ArrowRight' || e.key === 'd') next()
  else if (e.key === 'ArrowLeft' || e.key === 'a') prev()
  else if (e.key === 's' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); saveCurrentFrame() }
  else if (e.key === 'z' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); undoLastBox() }
  else if (e.key === 'n' || e.key === 'N') skipFrame()
}

// ─── Watchers ───────────────────────────────────────────────

watch(currentIdx, async () => {
  imageError.value = false
  const frame = frames.value[currentIdx.value]
  await maybeLoadLabels(frame)
  await loadImageWithAuth()
})

// ─── Lifecycle ──────────────────────────────────────────────

let resizeObserver = null

onMounted(() => {
  loadFrames()
  window.addEventListener('keydown', handleKeydown)
  resizeObserver = new ResizeObserver(() => nextTick(draw))
  if (canvasContainer.value) resizeObserver.observe(canvasContainer.value)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown)
  if (resizeObserver) resizeObserver.disconnect()
  if (currentImageUrl.value) {
    URL.revokeObjectURL(currentImageUrl.value)
  }
})
</script>

<style scoped>
.dice-labeler {
  position: fixed;
  top: 48px; left: 0; right: 0; bottom: 0;
  display: flex;
  flex-direction: column;
  background: #0a0a0a;
  color: #e0e0e0;
  font-family: 'Segoe UI', system-ui, sans-serif;
  font-size: 13px;
}

.access-denied {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #0a0a0a;
  z-index: 100;
}
.access-denied .access-icon { font-size: 48px; margin-bottom: 16px; }
.access-denied h2 { color: #e0e0e0; margin-bottom: 8px; }
.access-denied p { color: #888; font-size: 14px; }
.access-denied .hint { color: #555; font-size: 12px; margin-top: 4px; }

.header-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 24px;
  background: #111;
  border-bottom: 1px solid #222;
  flex-shrink: 0;
}
.header-left { display: flex; align-items: center; gap: 12px; }
.header-right { display: flex; align-items: center; gap: 8px; }
.logo { color: #22c55e; font-size: 18px; }
.title { font-weight: 700; font-size: 15px; }
.counter { color: #888; }
.stat { color: #888; font-size: 12px; }
.stat.green { color: #22c55e; }
.btn-nav {
  background: #333; color: #ccc; border: none; border-radius: 6px;
  padding: 6px 14px; font-weight: 600; font-size: 12px; cursor: pointer;
  text-decoration: none;
}
.btn-nav:hover { background: #444; }

.main-content {
  display: flex;
  flex: 1;
  overflow: hidden;
}

.canvas-area {
  flex: 1;
  position: relative;
  background: #080808;
  overflow: hidden;
}
.canvas-area canvas {
  position: absolute;
  top: 0; left: 0;
  width: 100%;
  height: 100%;
  cursor: crosshair;
}

.error-overlay {
  position: absolute; top: 0; left: 0; right: 0; bottom: 0;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
}
.error-title { color: #ef4444; font-size: 18px; margin-bottom: 8px; }
.error-url { color: #666; font-size: 11px; max-width: 500px; word-break: break-all; text-align: center; }
.error-hint { color: #555; font-size: 11px; margin-top: 8px; }

.box-overlay {
  position: absolute; bottom: 12px; left: 12px;
  background: rgba(0,0,0,0.8); padding: 4px 12px; border-radius: 4px;
  display: flex; gap: 8px; font-size: 13px; font-weight: 600;
}

.save-badge {
  position: absolute; top: 12px; right: 12px;
  padding: 3px 10px; border-radius: 4px;
  font-size: 11px; font-weight: 700; text-transform: uppercase;
}
.save-badge.saved { background: #22c55e; color: #000; }
.save-badge.skipped { background: #666; color: #fff; }
.save-badge.local { background: #f59e0b; color: #000; }

.loading-label {
  position: absolute; top: 50%; left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(0,0,0,0.8); padding: 8px 16px; border-radius: 6px;
  color: #888; font-size: 12px;
}

.right-panel {
  width: 250px;
  flex-shrink: 0;
  background: #151515;
  border-left: 1px solid #222;
  padding: 12px;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

.section-label {
  color: #666; font-size: 10px; text-transform: uppercase;
  letter-spacing: 1px; margin-bottom: 6px; margin-top: 12px;
}
.section-label:first-child { margin-top: 0; }

.dice-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 4px;
}
.dice-btn {
  display: flex; flex-direction: column; align-items: center;
  padding: 6px 0; border: 1px solid #333; border-radius: 6px;
  background: #1a1a1a; color: #888; cursor: pointer; transition: all 0.15s;
}
.dice-btn.active { color: #000; font-weight: 700; }
.dice-btn .pip { font-size: 20px; }
.dice-btn .key { font-size: 10px; color: inherit; opacity: 0.7; }

.dist-row { display: flex; align-items: center; gap: 6px; margin-bottom: 3px; }
.dist-label { width: 24px; font-size: 11px; font-weight: 600; }
.dist-bar-bg { flex: 1; height: 8px; background: #1a1a1a; border-radius: 4px; overflow: hidden; }
.dist-bar { height: 100%; border-radius: 4px; transition: width 0.3s; }
.dist-count { width: 24px; text-align: right; font-size: 11px; color: #555; }

.actions { display: flex; flex-direction: column; gap: 6px; margin-top: 12px; }
.btn {
  display: block; width: 100%; padding: 8px; border: none; border-radius: 6px;
  font-size: 12px; font-weight: 600; cursor: pointer; text-align: center;
  font-family: inherit;
}
.btn:disabled { opacity: 0.4; }
.btn.blue { background: #3b82f6; color: #fff; }
.btn.grey { background: #555; color: #fff; }
.btn.dark { background: #333; color: #ccc; }

.nav-row { display: flex; gap: 6px; }
.nav-row .btn { flex: 1; }

.scrubber {
  width: 100%; margin-top: 8px; accent-color: #22c55e;
}

.shortcuts {
  margin-top: 12px; color: #444; font-size: 10px; line-height: 1.8;
}
.shortcuts kbd {
  color: #777; font-family: inherit;
}

.snackbar {
  position: fixed; bottom: 20px; right: 20px;
  padding: 10px 20px; border-radius: 8px;
  font-size: 13px; font-weight: 600; z-index: 9999;
  animation: fadeIn 0.2s;
}
.snackbar.green { background: #22c55e; color: #000; }
.snackbar.orange { background: #f59e0b; color: #000; }

@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
</style>